/**
 * Hộp thoại của màn Người dùng — bám `users.html`:
 *   `#modal-invite`      Mời người dùng
 *   `#modal-user-detail` Chi tiết người dùng (Bảo mật · Phạm vi tên miền ·
 *                        Hồ sơ & vai trò · Hành động quản trị)
 * và hộp xác nhận xoá gõ đúng email (FR-054).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, apiWithMeta, thanhApiError, useLoiApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { avatarTone, initialsOf, useFormat } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { ConfirmName, Modal } from '../../components/Modal';
import { Field, SelectField } from '../../components/Field';
import { ApiErrorAlert } from '../../components/ApiErrorAlert';
import { SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';
import type { UserDetail, UserListItem, UserRoleRef, UsersPayload } from './types';

/* ─────────────────────────────────────────────────────────────
 * FR-052 · Mời người dùng — `#modal-invite`
 * ───────────────────────────────────────────────────────────── */

export function InviteUserModal({
  roles,
  onClose,
  onDone,
}: {
  roles: UserRoleRef[];
  onClose: () => void;
  onDone: (email: string) => void | Promise<void>;
}) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState(
    roles.find((role) => role.slug === 'member')?.id ?? roles[0]?.id ?? '',
  );
  const [message, setMessage] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const mutation = useMutation({
    mutationFn: () =>
      api.post<UserDetail>('/users/invite', {
        email,
        roleId,
        message: message.trim() || null,
        expiresInDays,
      }),
    onSuccess: (data) => void onDone(data.email),
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('users.inviteModal.title')}
      subtitle={t('users.inviteModal.subtitle')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('users.inviteModal.submit')}
          </button>
        </>
      }
    >
      <div data-od-id="modal-invite">
        <ApiErrorAlert error={error} />

        <Field
          label={t('users.inviteModal.email')}
          type="email"
          value={email}
          placeholder="ten@vnhost.vn"
          required
          autoFocus
          onChange={(event) => setEmail(event.target.value)}
          error={error?.fieldMessage('email')}
        />

        <div className="grid g-2">
          <SelectField
            label={t('users.inviteModal.role')}
            value={roleId}
            hint={t('users.inviteModal.roleHint')}
            required
            onChange={(event) => setRoleId(event.target.value)}
            error={error?.fieldMessage('roleId')}
          >
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </SelectField>

          <Field
            label={t('users.inviteModal.expires')}
            type="number"
            min={1}
            max={90}
            value={expiresInDays}
            onChange={(event) => setExpiresInDays(Number(event.target.value))}
            error={error?.fieldMessage('expiresInDays')}
          />
        </div>

        <Field
          label={t('users.inviteModal.message')}
          value={message}
          hint={t('common.optional')}
          onChange={(event) => setMessage(event.target.value)}
          error={error?.fieldMessage('message')}
        />

        <div className="scope-note">
          <Icon name="info" size={17} />
          <span>{t('users.inviteModal.note')}</span>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────
 * FR-052b · Tạo tài khoản trực tiếp — không qua lời mời
 * ───────────────────────────────────────────────────────────── */

/** Kết quả mọi thao tác trả về mật khẩu rõ (tạo mới · đặt lại). */
interface KetQuaMatKhau {
  email: string;
  password: string;
  mustChangePassword?: boolean;
  resetLinkSent: boolean;
  expiresInMinutes?: number;
}

/**
 * Ô mật khẩu dùng chung cho hai luồng quản trị.
 *
 * Bỏ trống = hệ thống sinh. Nút "Sinh mật khẩu" điền sẵn một chuỗi mạnh để
 * quản trị viên **thấy trước khi gửi** — nếu chỉ để trống rồi bấm gửi, họ phải
 * đợi máy chủ trả về mới biết mật khẩu là gì, và lúc ấy không sửa được nữa.
 */
function OMatKhau({
  value,
  onChange,
  error,
  hint,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  hint?: string | undefined;
}) {
  const t = useT();
  const [hien, setHien] = useState(true);

  return (
    <div className={error ? 'field invalid' : 'field'}>
      <label className="label" htmlFor="mat-khau-quan-tri">
        {t('users.password.label')}
      </label>
      <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
        <input
          id="mat-khau-quan-tri"
          className={error ? 'invalid' : undefined}
          type={hien ? 'text' : 'password'}
          value={value}
          placeholder={t('users.password.placeholder')}
          autoComplete="new-password"
          spellCheck={false}
          style={{ flex: 1, fontFamily: value ? 'var(--mono, monospace)' : undefined }}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          className="btn btn-sm"
          type="button"
          title={hien ? t('users.password.hide') : t('users.password.show')}
          onClick={() => setHien((v) => !v)}
        >
          <Icon name={hien ? 'eye-off' : 'eye'} size={16} />
        </button>
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => onChange(sinhMatKhau())}
          data-od-id="btn-sinh-mat-khau"
        >
          <Icon name="refresh" size={16} />
          {t('users.password.generate')}
        </button>
      </div>
      {error ? <p className="err">{error}</p> : hint ? <p className="hint">{hint}</p> : null}
    </div>
  );
}

/**
 * Sinh mật khẩu phía trình duyệt — CHỈ để hiện trước cho quản trị viên.
 *
 * Máy chủ vẫn kiểm lại bằng `assertPasswordAcceptable`, nên chuỗi này không
 * được tin là hợp lệ; nó chỉ tiết kiệm một vòng gọi. Bỏ ký tự dễ đọc nhầm
 * (`O0lI1S5B8`) vì mật khẩu này sẽ được đọc lại cho người dùng qua điện thoại.
 */
function sinhMatKhau(): string {
  const hoa = 'ACDEFGHJKLMNPQRTUVWXYZ';
  const thuong = 'abcdefghijkmnopqrstuvwxyz';
  const so = '234679';
  const dacBiet = '!@#$%^&*?-_=+';
  const kho = hoa + thuong + so + dacBiet;

  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  const chon = (bang: string, i: number): string => bang[bytes[i]! % bang.length] as string;

  const ket = [chon(hoa, 0), chon(thuong, 1), chon(so, 2), chon(dacBiet, 3)];
  for (let i = 4; i < 16; i++) ket.push(chon(kho, i));

  for (let i = ket.length - 1; i > 0; i--) {
    const j = bytes[i]! % (i + 1);
    [ket[i], ket[j]] = [ket[j] as string, ket[i] as string];
  }
  return ket.join('');
}

/**
 * Khối hiện mật khẩu sau khi thao tác thành công, kèm nút chép.
 *
 * `viec` quyết định câu chào — OQ-324.
 *
 * Khối này dùng chung cho hai hộp thoại, nhưng câu mở đầu vốn cứng là *"Đã đổi
 * mật khẩu cho … thành công"*. Khi quản trị viên vừa TẠO tài khoản thì câu ấy
 * sai: chưa từng có mật khẩu nào để mà đổi. Ảnh chủ dự án gửi có đúng mâu thuẫn
 * đó — tiêu đề "Đã tạo tài khoản", ngay dưới là "Đã đổi mật khẩu cho …".
 */
function KhoiMatKhauMoi({ ketQua, viec }: { ketQua: KetQuaMatKhau; viec: 'tao' | 'doi' }) {
  const t = useT();
  const { toastWarn } = useToast();
  const [daChep, setDaChep] = useState(false);

  const chep = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(ketQua.password);
      setDaChep(true);
      setTimeout(() => setDaChep(false), 2000);
    } catch {
      toastWarn(t('users.password.copyFailed'));
    }
  };

  return (
    <div data-od-id="khoi-mat-khau-moi">
      <div className="form-alert ok">
        <Icon name="check" size={17} />
        <span>
          {viec === 'tao'
            ? t('users.password.createdSuccess', { email: ketQua.email })
            : t('users.password.success', { email: ketQua.email })}
        </span>
      </div>

      <div className="field">
        <span className="label">{t('users.password.newPassword')}</span>
        <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
          <input
            readOnly
            value={ketQua.password}
            style={{ flex: 1, fontFamily: 'var(--mono, monospace)' }}
            onFocus={(event) => event.currentTarget.select()}
          />
          <button className="btn btn-sm" type="button" onClick={() => void chep()}>
            <Icon name={daChep ? 'check' : 'copy'} size={16} />
            {daChep ? t('users.password.copied') : t('common.copy')}
          </button>
        </div>
        <p className="hint">{t('users.password.onceOnly')}</p>
      </div>

      <div className={ketQua.resetLinkSent ? 'scope-note' : 'scope-note warn'}>
        <Icon name={ketQua.resetLinkSent ? 'info' : 'warn'} size={17} />
        <span>
          {ketQua.resetLinkSent
            ? t('users.password.linkSent', {
                hours: Math.round((ketQua.expiresInMinutes ?? 1440) / 60),
              })
            : t('users.password.linkFailed')}
        </span>
      </div>

      {ketQua.mustChangePassword ? (
        <div className="scope-note">
          <Icon name="info" size={17} />
          <span>{t('users.password.mustChangeNote')}</span>
        </div>
      ) : null}
    </div>
  );
}

export function CreateUserModal({
  roles,
  onClose,
  onDone,
}: {
  roles: UserRoleRef[];
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleId, setRoleId] = useState(
    roles.find((role) => role.slug === 'member')?.id ?? roles[0]?.id ?? '',
  );
  const [password, setPassword] = useState('');
  const [mustChange, setMustChange] = useState(true);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();
  const [ketQua, setKetQua] = useState<KetQuaMatKhau | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ user: UserDetail; password: string; resetLinkSent: boolean }>('/users', {
        email,
        roleId,
        fullName: fullName.trim() || null,
        password: password.trim() || null,
        mustChangePassword: mustChange,
      }),
    onSuccess: (data) => {
      setError(null);
      setKetQua({
        email: data.user.email,
        password: data.password,
        mustChangePassword: mustChange,
        resetLinkSent: data.resetLinkSent,
        expiresInMinutes: 24 * 60,
      });
      void onDone();
    },
    onError: (err) => setError(thanhApiError(err)),
  });

  if (ketQua) {
    return (
      <Modal
        open
        title={t('users.createModal.doneTitle')}
        onClose={onClose}
        footer={
          <button className="btn btn-primary" type="button" onClick={onClose}>
            {t('common.close')}
          </button>
        }
      >
        <KhoiMatKhauMoi ketQua={ketQua} viec="tao" />
      </Modal>
    );
  }

  return (
    <Modal
      open
      title={t('users.createModal.title')}
      subtitle={t('users.createModal.subtitle')}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('users.createModal.submit')}
          </button>
        </>
      }
    >
      <div data-od-id="modal-create-user">
        <ApiErrorAlert error={error} />

        <Field
          label={t('users.createModal.email')}
          type="email"
          value={email}
          placeholder="ten@vnhost.vn"
          required
          autoFocus
          onChange={(event) => setEmail(event.target.value)}
          error={error?.fieldMessage('email')}
        />

        <div className="grid g-2">
          <Field
            label={t('users.createModal.fullName')}
            value={fullName}
            hint={t('common.optional')}
            onChange={(event) => setFullName(event.target.value)}
            error={error?.fieldMessage('fullName')}
          />

          <SelectField
            label={t('users.createModal.role')}
            value={roleId}
            required
            onChange={(event) => setRoleId(event.target.value)}
            error={error?.fieldMessage('roleId')}
          >
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </SelectField>
        </div>

        <OMatKhau
          value={password}
          onChange={setPassword}
          error={error?.fieldMessage('newPassword') ?? error?.fieldMessage('password')}
          hint={t('users.password.hint')}
        />

        <label className="row" style={{ gap: 8, cursor: 'pointer', marginBottom: 12 }}>
          <input
            className="check"
            type="checkbox"
            checked={mustChange}
            onChange={(event) => setMustChange(event.target.checked)}
            data-od-id="chk-buoc-doi-mat-khau"
          />
          <span className="small">{t('users.password.mustChange')}</span>
        </label>

        <div className="scope-note">
          <Icon name="info" size={17} />
          <span>{t('users.createModal.note')}</span>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────
 * FR-053 · Đặt lại mật khẩu — quản trị viên đặt trực tiếp
 * ───────────────────────────────────────────────────────────── */

export function ResetPasswordModal({
  userId,
  email,
  onClose,
  onDone,
}: {
  userId: string;
  email: string;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const t = useT();
  /* Sinh sẵn một mật khẩu ngay khi mở: quản trị viên thấy được thứ mình sắp gửi
   * trước khi bấm, thay vì phải chờ máy chủ trả về rồi mới biết. */
  const [password, setPassword] = useState(() => sinhMatKhau());
  const [mustChange, setMustChange] = useState(true);
  const { error, setError, xoaLoiKhiGo } = useLoiApi();
  const [ketQua, setKetQua] = useState<KetQuaMatKhau | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<KetQuaMatKhau & { mustChangePassword: boolean }>(`/users/${userId}/reset-password`, {
        password: password.trim() || null,
        mustChangePassword: mustChange,
      }),
    onSuccess: (data) => {
      setError(null);
      setKetQua(data);
      void onDone();
    },
    onError: (err) => setError(thanhApiError(err)),
  });

  if (ketQua) {
    return (
      <Modal
        open
        title={t('users.resetModal.doneTitle')}
        onClose={onClose}
        footer={
          <button className="btn btn-primary" type="button" onClick={onClose}>
            {t('common.close')}
          </button>
        }
      >
        <KhoiMatKhauMoi ketQua={ketQua} viec="doi" />
      </Modal>
    );
  }

  return (
    <Modal
      open
      title={t('users.resetModal.title')}
      subtitle={email}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('common.processing') : t('users.resetModal.submit')}
          </button>
        </>
      }
    >
      <div data-od-id="modal-reset-password">
        <ApiErrorAlert error={error} />

        <OMatKhau
          value={password}
          onChange={setPassword}
          error={error?.fieldMessage('newPassword') ?? error?.fieldMessage('password')}
          hint={t('users.password.hint')}
        />

        <label className="row" style={{ gap: 8, cursor: 'pointer', marginBottom: 12 }}>
          <input
            className="check"
            type="checkbox"
            checked={mustChange}
            onChange={(event) => setMustChange(event.target.checked)}
            data-od-id="chk-buoc-doi-mat-khau"
          />
          <span className="small">{t('users.password.mustChange')}</span>
        </label>

        <div className="scope-note warn">
          <Icon name="warn" size={17} />
          <span>{t('users.resetModal.note')}</span>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────
 * FR-053 · Chi tiết người dùng — `#modal-user-detail`
 * ───────────────────────────────────────────────────────────── */

export function UserDetailModal({
  userId,
  roles,
  onClose,
  onChanged,
  onDelete,
}: {
  userId: string;
  roles: UserRoleRef[];
  onClose: () => void;
  onChanged: () => void | Promise<void>;
  onDelete: (target: UserListItem) => void;
}) {
  const { formatDateTime } = useFormat();
  const t = useT();
  const { user: me } = useAuth();
  const { toast, toastError } = useToast();

  const detailQuery = useQuery({
    queryKey: ['users', userId],
    queryFn: () => api.get<UserDetail>(`/users/${userId}`),
  });
  const detail = detailQuery.data;

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [roleId, setRoleId] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();
  const [moDatMatKhau, setMoDatMatKhau] = useState(false);

  useEffect(() => {
    if (!detail) return;
    setFullName(detail.fullName);
    setPhone(detail.phone ?? '');
    setRoleId(detail.role.id);
  }, [detail?.id]);

  const refresh = async () => {
    await detailQuery.refetch();
    await onChanged();
  };

  const onError = (err: unknown) => {
    setError(thanhApiError(err));
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));
  };

  const saveProfile = useMutation({
    mutationFn: () =>
      api.patch<UserListItem>(`/users/${userId}`, {
        fullName,
        phone: phone.trim() || null,
        ...(roleId && roleId !== detail?.role.id ? { roleId } : {}),
      }),
    onSuccess: async () => {
      setError(null);
      await refresh();
      toast(t('users.toast.saved'));
    },
    onError,
  });

  const action = useMutation({
    mutationFn: (input: { path: string; method: 'post' | 'del'; message: string }) =>
      (input.method === 'post' ? api.post(input.path) : api.del(input.path)).then(
        () => input.message,
      ),
    onSuccess: async (message) => {
      await refresh();
      toast(message);
    },
    onError,
  });

  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'suspended') =>
      api.patch<UserListItem>(`/users/${userId}`, { status }),
    onSuccess: async () => {
      await refresh();
      toast(t('users.toast.saved'));
    },
    onError,
  });

  const busy = saveProfile.isPending || action.isPending || setStatus.isPending;
  const isSelf = detail?.id === me?.id;

  return (
    <Modal
      onFieldChange={xoaLoiKhiGo.onChange}
      open
      wide
      title={detail ? detail.fullName || detail.email : t('users.detail.title')}
      subtitle={
        detail
          ? `${detail.email} · ${detail.role.name} · ${t('users.detail.joined', {
              date: formatDateTime(detail.createdAt),
            })}`
          : undefined
      }
      onClose={onClose}
      footer={
        <>
          <span className="grow small muted">{t('users.detail.foot')}</span>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.close')}
          </button>
        </>
      }
    >
      {!detail ? (
        <SkeletonCard rows={5} />
      ) : (
        <div data-od-id="modal-user-detail">
          <ApiErrorAlert error={error} />

          <div className="grid g-2">
            <div className="stack">
              <span className="eyebrow">{t('users.detail.security')}</span>
              <dl className="kv">
                <dt>2FA</dt>
                <dd>
                  {detail.twofaEnabled ? (
                    <span className="chip ok">
                      <span className="dot" />
                      TOTP
                    </span>
                  ) : detail.twofaRequired ? (
                    <span className="chip warn">
                      <span className="dot" />
                      {t('users.twofa.required')}
                    </span>
                  ) : (
                    <span className="chip warn">
                      <span className="dot" />
                      {t('users.twofa.off')}
                    </span>
                  )}
                </dd>
                <dt>{t('users.detail.lastLogin')}</dt>
                <dd className="num">
                  {detail.lastLoginAt ? formatDateTime(detail.lastLoginAt) : t('users.neverLogin')}
                </dd>
                <dt>{t('users.detail.sessions')}</dt>
                <dd className="num">
                  {t('users.detail.sessionCount', { count: detail.sessions.length })}
                </dd>
                <dt>{t('users.detail.passwordChanged')}</dt>
                <dd className="num">
                  {detail.passwordChangedAt
                    ? formatDateTime(detail.passwordChangedAt)
                    : t('common.never')}
                </dd>
              </dl>
            </div>

            <div className="stack">
              <span className="eyebrow">{t('users.detail.scope')}</span>
              {detail.domains.length > 0 ? (
                <div className="row">
                  {detail.domains.map((domain) => (
                    <span key={domain.id} className="chip accent">
                      {domain.name}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="small muted">{t('users.detail.noDomain')}</span>
              )}
              <Link className="btn btn-sm" to="/permissions" style={{ justifySelf: 'start' }}>
                {t('users.detail.openPermissions')}
              </Link>
            </div>
          </div>

          <hr className="divider" />
          <span className="eyebrow">{t('users.detail.profile')}</span>
          <div className="grid g-2 mt-14">
            <Field
              label={t('users.detail.fullName')}
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              error={error?.fieldMessage('fullName')}
            />
            <Field
              label={t('users.detail.phone')}
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              error={error?.fieldMessage('phone')}
            />
          </div>
          <div className="grid g-2">
            <SelectField
              label={t('users.detail.role')}
              value={roleId}
              disabled={isSelf}
              hint={isSelf ? t('users.detail.selfRoleNote') : undefined}
              onChange={(event) => setRoleId(event.target.value)}
              error={error?.fieldMessage('roleId')}
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </SelectField>
            <div className="field" style={{ justifyContent: 'flex-end' }}>
              <button
                className="btn btn-primary"
                type="button"
                disabled={busy}
                onClick={() => saveProfile.mutate()}
              >
                {saveProfile.isPending ? t('common.processing') : t('common.saveChanges')}
              </button>
            </div>
          </div>

          <hr className="divider" />
          <span className="eyebrow">{t('users.detail.adminActions')}</span>
          <div className="row mt-14">
            <button
              className="btn btn-sm btn-primary"
              type="button"
              disabled={busy}
              onClick={() => setMoDatMatKhau(true)}
              data-od-id="btn-dat-lai-mat-khau"
            >
              <Icon name="key" size={16} />
              {t('users.detail.resetPassword')}
            </button>
            <button
              className="btn btn-sm"
              type="button"
              disabled={busy}
              onClick={() =>
                action.mutate({
                  path: `/users/${userId}/reset-password`,
                  method: 'post',
                  message: t('users.toast.resetSent'),
                })
              }
            >
              {t('users.detail.sendReset')}
            </button>
            <button
              className="btn btn-sm"
              type="button"
              disabled={busy}
              onClick={() =>
                action.mutate({
                  path: `/users/${userId}/sessions`,
                  method: 'del',
                  message: t('users.toast.sessionsRevoked'),
                })
              }
            >
              {t('users.detail.endSessions')}
            </button>
            <button
              className="btn btn-sm"
              type="button"
              disabled={busy || !detail.twofaEnabled}
              onClick={() =>
                action.mutate({
                  path: `/users/${userId}/2fa`,
                  method: 'del',
                  message: t('users.toast.twofaDisabled'),
                })
              }
            >
              {t('users.detail.reset2fa')}
            </button>
            <button
              className="btn btn-sm"
              type="button"
              disabled={busy || detail.twofaEnabled || detail.twofaRequired}
              onClick={() =>
                action.mutate({
                  path: `/users/${userId}/force-2fa`,
                  method: 'post',
                  message: t('users.toast.force2fa'),
                })
              }
            >
              {t('users.detail.force2fa')}
            </button>
            <button
              className="btn btn-sm btn-danger"
              type="button"
              disabled={busy || isSelf}
              onClick={() =>
                setStatus.mutate(detail.status === 'suspended' ? 'active' : 'suspended')
              }
            >
              {detail.status === 'suspended'
                ? t('users.detail.activate')
                : t('users.detail.suspend')}
            </button>
            <button
              className="btn btn-sm btn-danger"
              type="button"
              disabled={busy || isSelf}
              onClick={() => onDelete(detail)}
            >
              {t('users.detail.delete')}
            </button>
          </div>
        </div>
      )}

      {moDatMatKhau && detail ? (
        <ResetPasswordModal
          userId={userId}
          email={detail.email}
          onClose={() => setMoDatMatKhau(false)}
          onDone={refresh}
        />
      ) : null}
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────
 * FR-054 · Xoá người dùng — xác nhận bằng cách gõ email
 * ───────────────────────────────────────────────────────────── */

export function DeleteUserModal({
  target,
  onClose,
  onDone,
}: {
  target: UserListItem;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const t = useT();
  const [confirmEmail, setConfirmEmail] = useState('');
  const [transferToUserId, setTransferToUserId] = useState('');
  const { error, setError, xoaLoiKhiGo } = useLoiApi();

  const candidatesQuery = useQuery({
    queryKey: ['users', 'transfer-candidates'],
    queryFn: () => apiWithMeta<UsersPayload>('/users?status=active&perPage=100'),
    enabled: target.domainCount > 0,
  });

  const candidates = (candidatesQuery.data?.data.items ?? []).filter(
    (item) => item.id !== target.id,
  );

  useEffect(() => {
    if (!transferToUserId && candidates[0]) setTransferToUserId(candidates[0].id);
  }, [candidates.length]);

  const mutation = useMutation({
    mutationFn: () =>
      api.del(`/users/${target.id}`, {
        confirmEmail,
        ...(target.domainCount > 0 ? { transferToUserId } : {}),
      }),
    onSuccess: () => void onDone(),
    onError: (err) => setError(thanhApiError(err)),
  });

  return (
    <Modal
      open
      title={t('users.deleteModal.title')}
      subtitle={t('users.deleteModal.subtitle', { email: target.email })}
      onClose={onClose}
      onFieldChange={xoaLoiKhiGo.onChange}
      onSubmit={() => mutation.mutate()}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-danger"
            type="submit"
            disabled={mutation.isPending || confirmEmail.trim().toLowerCase() !== target.email}
          >
            {mutation.isPending ? t('common.processing') : t('users.deleteModal.submit')}
          </button>
        </>
      }
    >
      <ApiErrorAlert error={error} />

      {target.domainCount > 0 ? (
        <>
          <div className="form-alert warn">
            <Icon name="info" size={16} />
            <span>{t('users.deleteModal.ownsDomains', { count: target.domainCount })}</span>
          </div>
          <SelectField
            label={t('users.deleteModal.transferTo')}
            value={transferToUserId}
            required
            onChange={(event) => setTransferToUserId(event.target.value)}
            error={error?.fieldMessage('transferToUserId')}
          >
            {candidates.map((item) => (
              <option key={item.id} value={item.id}>
                {item.fullName || item.email} · {item.email}
              </option>
            ))}
          </SelectField>
        </>
      ) : null}

      <div className="row" style={{ gap: 10 }}>
        <span className={`avatar sm ${avatarTone(target.id)}`}>
          {initialsOf(target.fullName, target.email)}
        </span>
        <span className="small muted">{target.fullName || target.email}</span>
      </div>

      <ConfirmName
        label={t('users.deleteModal.confirmLabel')}
        expected={target.email}
        value={confirmEmail}
        onChange={setConfirmEmail}
      />

      {error?.fieldMessage('confirmEmail') ? (
        <span className="err">{error.fieldMessage('confirmEmail')}</span>
      ) : null}
    </Modal>
  );
}
